import {
  SubscriptionIntervals,
  SubscriptionProvider,
  SubscriptionStatus,
  type Subscription,
} from "@prisma/client";
import type Stripe from "stripe";

import {
  STRIPE_PRICE_ID_TO_INTERVAL_MAP,
  STRIPE_PRICE_ID_TO_PLAN_MAP,
  STRIPE_PRICE_ID_TO_PRICE_MAP,
  isStripeConsumerPriceId,
  type TStripePriceId,
} from "@musicfy/contants/StripeSubscriptions";

/** Unpaid first-invoice checkout states — never grant Packages access. */
export const isIncompleteStripeSubscriptionStatus = (
  status: Stripe.Subscription.Status
): boolean => status === "incomplete" || status === "incomplete_expired";

const mapStripeStatus = (
  status: Stripe.Subscription.Status
): SubscriptionStatus => {
  switch (status) {
    case "active":
      return SubscriptionStatus.active;
    case "trialing":
      return SubscriptionStatus.trialing;
    case "past_due":
    case "unpaid":
      return SubscriptionStatus.past_due;
    case "canceled":
    case "incomplete":
    case "incomplete_expired":
      // Incomplete must never map to active (historical footgun). Upsert still
      // skips these; canceled is the non-entitling fallback if extract is used.
      return SubscriptionStatus.canceled;
    case "paused":
      return SubscriptionStatus.paused;
    default:
      return SubscriptionStatus.canceled;
  }
};

export const getStripeConsumerPriceId = (
  subscription: Stripe.Subscription
): TStripePriceId | null => {
  for (const item of subscription.items.data) {
    const priceId =
      typeof item.price === "string" ? item.price : item.price?.id;
    if (priceId && isStripeConsumerPriceId(priceId)) {
      return priceId;
    }
  }
  return null;
};

export const isStripeConsumerSubscription = (
  subscription: Stripe.Subscription
): boolean => getStripeConsumerPriceId(subscription) !== null;

/**
 * Map a Stripe Subscription object into our Prisma Subscription shape.
 * Returns null when the price is not a consumer Packages price (e.g. API Access).
 */
export const extractStripeSubscriptionData = (
  data: Stripe.Subscription,
  userIdOverride?: string
): Subscription | null => {
  const priceId = getStripeConsumerPriceId(data);
  if (!priceId) {
    return null;
  }

  const userId =
    userIdOverride ||
    data.metadata?.userId ||
    (typeof data.metadata?.user_id === "string"
      ? data.metadata.user_id
      : undefined);

  if (!userId) {
    return null;
  }

  const plan = STRIPE_PRICE_ID_TO_PLAN_MAP[priceId];
  const interval = STRIPE_PRICE_ID_TO_INTERVAL_MAP[priceId];
  const price = STRIPE_PRICE_ID_TO_PRICE_MAP[priceId];

  const customerId =
    typeof data.customer === "string" ? data.customer : data.customer.id;

  const periodStart = data.current_period_start
    ? new Date(data.current_period_start * 1000)
    : null;
  const periodEnd = data.current_period_end
    ? new Date(data.current_period_end * 1000)
    : null;

  const subscriptionData: Subscription = {
    plan,
    interval:
      interval === SubscriptionIntervals.year
        ? SubscriptionIntervals.year
        : SubscriptionIntervals.month,
    status: mapStripeStatus(data.status),
    price,
    currency: "USD",
    periodEnd,
    periodStart,
    createdAt: new Date(data.created * 1000),
    updatedAt: new Date(),
    canceledAt: data.cancel_at_period_end
      ? new Date((data.cancel_at ?? data.current_period_end) * 1000)
      : data.canceled_at
        ? new Date(data.canceled_at * 1000)
        : null,
    pendingPlan: null,
    pendingInterval: null,
    pendingChangeAt: null,
    stripeScheduleId: null,
    customerId,
    subscriptionId: data.id,
    userId,
    additionalVoiceSlots: 0,
    freeVoiceSlots: 0,
    provider: SubscriptionProvider.stripe,
  };

  return subscriptionData;
};
